import { EnrolFirstKeyForm } from '@hurdl/authentication/frontend';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Icon,
  Stack,
} from '@hurdl/design-system';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { type ReactElement } from 'react';

import { Logo } from '../brand/logo.js';

/**
 * Props the enrol page takes — the one-time ticket read from the address.
 *
 * The bootstrap script prints an `/enrol?ticket=...` link at the deployment's WebAuthn
 * origin (ADR 0050), so the operator who follows it arrives with the ticket already in
 * the address; the route reads it from the query and hands it here. Absent — the operator
 * typed `/enrol` by hand, or pasted only the origin — it is the empty string, and the
 * form renders an empty ticket field for them to paste into.
 */
export interface EnrolPageProps {
  readonly ticket: string;
}

/**
 * The first-key enrolment screen — where the bootstrap administrator registers the one
 * key that turns a provisioned account into one that can sign in (ADR 0050, authentication
 * R8.1).
 *
 * **Anonymous by design.** It precedes the first sign-in: the bootstrap administrator
 * holds a one-time ticket and no session, so the route is reachable with no session and
 * the Route_Guard lets an anonymous operator rest on it (`auth-guard.ts`). The ticket in
 * the address is the credential; there is nothing to authenticate.
 *
 * It composes the capability's {@link EnrolFirstKeyForm}, which drives the WebAuthn
 * registration ceremony through the capability's store — no `fetch` and no address this
 * module holds (panel invariant 2) — and reaches the capability's provider mounted above
 * the whole tree in `main.tsx`. The ticket is pre-filled from the address; the operator
 * supplies a label for the key.
 *
 * It renders **outside the application shell**, like the Sole_Factor_Login: the Layout
 * detects the enrol route and renders this page alone, with no sidebar and no app bar,
 * because an operator who is not signed in has no navigation to offer. The composition
 * mirrors the login page — a centred `Card` on the themed page, the wordmark above it,
 * the key icon, a line of context — and declares no colour, spacing or type of its own,
 * respecting the Design_System's tokens and both Theme_Modes.
 *
 * **Enrolment starts no session** (R8.1), so a completed enrolment routes the operator to
 * the login to sign in with the key they just registered — what follows the enrolment is
 * the application's decision (R17.10), and the panel's is to send them where they sign in.
 */
export function EnrolPage({ ticket }: EnrolPageProps): ReactElement {
  const navigate = useNavigate();

  function handleEnrolled(): void {
    // The enrolment granted no session; the operator now signs in with the new key.
    void navigate({ to: '/login' });
  }

  return (
    // A centred column filling the themed page: the shell is absent here, so the page
    // owns its own vertical rhythm, exactly as the login page does.
    <div className="flex min-h-dvh w-full items-center justify-center p-hurdl-6">
      <Stack gap="large" align="centre" className="w-full max-w-sm">
        <Logo className="h-8 text-foreground" />

        <Card className="w-full">
          <CardHeader>
            <Stack gap="small" align="centre" className="text-center">
              <span className="text-hurdl-primary">
                <Icon
                  icon={KeyRound}
                  size="large"
                  label="Enrol a security key"
                />
              </span>
              <CardTitle>Enrol your first security key</CardTitle>
              <CardDescription>
                Register the hardware key this account will sign in with. Insert
                your key and follow your browser’s prompt. The enrolment ticket
                is single-use.
              </CardDescription>
            </Stack>
          </CardHeader>

          <CardContent>
            <EnrolFirstKeyForm ticket={ticket} onEnrolled={handleEnrolled} />
          </CardContent>
        </Card>
      </Stack>
    </div>
  );
}
